import React from 'react';
import { motion } from 'framer-motion';
import { Calendar } from 'lucide-react';

interface IrisMessageProps {
  step: number;
  totalSteps: number;
}

interface IrisMessageContent {
  title: string;
  message: string;
}

const IrisMessage: React.FC<IrisMessageProps> = ({ step, totalSteps }) => {
  const getIrisMessage = (currentStep: number): IrisMessageContent => {
    const messages: Record<number, IrisMessageContent> = {
      1: {
        title: "Planifions votre rendez-vous ! 📅",
        message: "Choisissez la date et l'heure qui vous conviennent le mieux. Je vous propose des créneaux disponibles pour une consultation personnalisée."
      },
      2: {
        title: "Parlez-moi de votre projet ! 💼",
        message: "Donnez-moi quelques détails sur vous et votre projet. Cela m'aidera à préparer notre entretien et à vous proposer les meilleures solutions."
      }
    };
    return messages[currentStep] || messages[1];
  };

  const progressPercent = Math.round((step / totalSteps) * 100);
  const message = getIrisMessage(step);

  return (
    <div className="mb-8">
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-100">
        <div className="flex items-start space-x-4">
          <div className="flex-shrink-0">
            <div className="relative">
              <div className="w-16 h-16 rounded-full overflow-hidden border-3 border-white shadow-lg">
                <img 
                  src="/irisavatar.jpg" 
                  alt="Iris IA" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full border-2 border-white flex items-center justify-center">
                <Calendar className="w-3 h-3 text-white" />
              </div>
            </div>
          </div>
          
          <div className="flex-1">
            <div className="bg-white rounded-xl p-4 shadow-sm relative">
              <div className="absolute left-0 top-4 transform -translate-x-2">
                <div className="w-4 h-4 bg-white rotate-45 border-l border-b border-gray-100"></div>
              </div>
              
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {message.title}
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {message.message}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Barre de progression */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-gray-700">Progression de la réservation</span>
          <span className="text-sm text-blue-600 font-semibold">{progressPercent}% complété</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <motion.div 
            className="bg-gradient-to-r from-blue-500 to-purple-500 h-3 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${progressPercent}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span className={step >= 1 ? 'text-blue-600 font-medium' : ''}>Date & Heure</span>
          <span className={step >= 2 ? 'text-blue-600 font-medium' : ''}>Informations</span>
        </div>
      </div>
    </div>
  );
};

export default IrisMessage;
